import moment from 'moment'
import { defineMessages, MessageDescriptor } from 'react-intl'

import {
  collectTimeDays,
  shippingOptions
} from 'app/utils/constants/options.constants'
import { SHIPPING_STATUS_TYPE } from 'app/utils/constants/status.constants'
import { transformIntlOptions } from 'app/utils/helpers/transforms.helper'

const msg = defineMessages({
  length: {
    defaultMessage: 'Length'
  },
  width: {
    defaultMessage: 'width'
  },
  height: {
    defaultMessage: 'height'
  },
  weight: {
    defaultMessage: 'Weight'
  },
  cm: {
    defaultMessage: 'cm'
  },
  kg: {
    defaultMessage: 'kg'
  }
})

export const formatDimensions = (
  shipping: ShippingModel,
  formatMessage: (intlObject: MessageDescriptor) => string,
  isShort?: boolean
) => {
  const values = [
    {
      name: msg.length,
      value: shipping?.length,
      uom: msg.cm
    },
    {
      name: msg.width,
      value: shipping?.width,
      uom: msg.cm
    },
    {
      name: msg.height,
      value: shipping?.height,
      uom: msg.cm
    }
  ]

  const dimensions = values
    .filter((item) => item.value)
    .map(
      (item) =>
        `${formatMessage(item.name)} ${item.value} ${formatMessage(item.uom)}`
    )

  const dimensionsShort = values
    .filter((item) => item.value)
    .map((item) => item.value)
    .join(' \u00D7 ')

  const formattedWeight = !!shipping?.weight
    ? `${formatMessage(msg.weight)} ${shipping?.weight} ${formatMessage(
        msg.kg
      )}`
    : ''

  return {
    dimensions: isShort ? dimensionsShort : dimensions.join(', '),
    formattedWeight
  }
}

export const formatLocation = ({
  addressObj,
  key,
  withoutZipCode
}: {
  addressObj?: ShippingModel | UserBySlugModel
  key?: 'collection_address' | 'dispatch_address'
  withoutZipCode?: boolean
}) => {
  const address =
    key && addressObj
      ? addressObj[key]
      : addressObj?.collection_address || addressObj?.dispatch_address

  if (!address) return null

  if (withoutZipCode)
    return [address.city, address.full_country_name].filter(Boolean).join(', ')

  return [address.zip_code, address.city, address.full_country_name]
    .filter(Boolean)
    .join(', ')
}

export const formatShippingTypes = (
  type: string,
  formatMessage: (intlObject: MessageDescriptor) => string
) => {
  const shippingOnlyOptions = shippingOptions.filter(
    (option) => option.value === SHIPPING_STATUS_TYPE.ship_only
  )

  const clickAndCollectOptions = shippingOptions.filter(
    (option) => option.value === SHIPPING_STATUS_TYPE.click_and_collect
  )

  const bothOptions = [...shippingOnlyOptions, ...clickAndCollectOptions]

  const shippingTypes =
    {
      [SHIPPING_STATUS_TYPE.both]: bothOptions,
      [SHIPPING_STATUS_TYPE.click_and_collect]: clickAndCollectOptions,
      [SHIPPING_STATUS_TYPE.ship_only]: shippingOnlyOptions
    }[type] || []

  return transformIntlOptions(shippingTypes, formatMessage)
}

export const transformCollectionDays = (
  daysArray: string[],
  formatMessage: (intlObject: MessageDescriptor) => string
) => {
  const dayOrder = Object.keys(collectTimeDays)

  const sortedDays = daysArray.sort(
    (a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b)
  )

  const resultStringArray = sortedDays.reduce((acc, day, index, array) => {
    const previousDay = array[index - 1]
    const isConsecutive =
      previousDay && dayOrder.indexOf(day) === dayOrder.indexOf(previousDay) + 1

    if (isConsecutive) {
      const lastGroup = acc.pop()

      if (lastGroup) {
        const firstDay = lastGroup.split(' - ')[0]
        const lastDay = formatMessage(collectTimeDays[day])

        acc.push(`${firstDay} - ${lastDay}`)
      }
    } else {
      acc.push(formatMessage(collectTimeDays[day]))
    }

    return acc
  }, [] as string[])

  const resultString = resultStringArray.join(', ')

  return resultString
}

export const transformCollectionTime = (timeFrom: string, timeTo: string) => {
  const transformedTimeFrom = moment(timeFrom).utc().format('h:mm a')
  const transformedTimeTo = moment(timeTo).utc().format('h:mm a')

  return `${transformedTimeFrom} - ${transformedTimeTo}`
}
